import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { createCheckoutSession, updateSubscription } from '../../lib/stripe';
import { products } from '../../stripe-config';
import { translations } from '../../translations';
import { supabase } from '../../lib/supabase';

interface CheckoutButtonProps {
  planName: keyof typeof products;
  isAnnual: boolean;
  language: string;
  children?: React.ReactNode;
  isDarkMode: boolean;
  accountId: string;
  currentPlan?: string | null;
  subscriptionId?: string | null;
  hasTrial?: string | null;
}

export function CheckoutButton({ 
  planName, 
  isAnnual, 
  language, 
  children, 
  isDarkMode, 
  accountId, 
  currentPlan,
  subscriptionId,
  hasTrial
}: CheckoutButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = translations[language as keyof typeof translations];

  const handleCheckout = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Determine currency based on language
      const currency = language === 'FR' ? 'EUR' : 'USD';
      const period = isAnnual ? 'annual' : 'monthly';

      // Get the product
      const product = products[planName];
      
      // Check if the product exists
      if (!product) {
        throw new Error(`Plan "${planName}" not found`);
      }

      // Check if the currency exists for this product
      if (!product[currency]) {
        throw new Error(`Currency "${currency}" not available for plan "${planName}"`);
      }

      // Check if the period exists for this currency
      if (!product[currency][period]) {
        throw new Error(`${period} pricing not available for plan "${planName}" in ${currency}`);
      }

      // Get the price ID
      const priceId = product[currency][period].priceId;
      
      if (!priceId) {
        throw new Error(`Price ID not found for ${planName} (${currency}, ${period})`);
      }

      // If we have a subscription ID, check if it's active
      if (subscriptionId) {
        const { data: subscription } = await supabase
          .from('stripe_subscriptions')
          .select('status')
          .eq('subscription_id', subscriptionId)
          .single();

        // If subscription is active, update it
        if (subscription?.status === 'active') {
          await updateSubscription(subscriptionId, priceId);
          window.location.reload();
          return;
        }
      }

      // Clear subscription_id from account if subscription is not active
      await supabase
        .from('instagram_accounts')
        .update({ subscription_id: null })
        .eq('id', accountId);

      // Create new checkout session
      await createCheckoutSession(priceId, product.mode, accountId, hasTrial === 'YES' ? false : true);

    } catch (error) {
      console.error('Error starting checkout:', error);
      setError(error instanceof Error ? error.message : 'Failed to start checkout');
    } finally {
      setIsLoading(false);
    }
  };

  // Get button text based on trial status
  const getButtonText = () => {
    if (isLoading) {
      return t.please_wait;
    }
    
    if (currentPlan) {
      return t.change_plan;
    }
    
    // If trial is YES, don't show "gratuitement" text
    if (hasTrial === 'YES') {
      return t.start_now_no_trial || t.start_now.replace(/gratuitement|for free|gratis|бесплатно/gi, '').trim();
    }
    
    return t.start_now;
  };
  return (
    <div className="w-full">
      <button
        onClick={handleCheckout}
        disabled={isLoading}
        className={`w-full mt-6 rounded-lg py-2 text-[13px] font-medium transition-colors flex items-center justify-center gap-2 ${
          isDarkMode
            ? 'bg-[#2b2b2b] text-gray-300 hover:bg-[#333333] disabled:bg-[#2b2b2b]'
            : 'bg-gray-50 text-gray-700 hover:bg-gray-100 disabled:bg-gray-50'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {isLoading ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            <span>{getButtonText()}</span>
          </>
        ) : (
          getButtonText()
        )}
      </button>
      {error && (
        <div className="mt-2 text-sm text-red-500">{error}</div>
      )}
    </div>
  );
}